/**
 * Age computation utilities for competition / audition eligibility and
 * the dancer's auto-assigned age division.
 *
 * Styles/categories separation (2026-06-04): the dancer's "category" is
 * their age division, auto-computed from `profiles.date_of_birth` at the
 * competition's age reference date (`auditions.age_cut_date`, defaulting
 * to Jan 1 of the event year) — see `resolveDancerDivision`. Style
 * entries (`competition_categories` rows) are free ticks across any
 * division; no per-entry age gating.
 */

import type { CompetitionAgeDivision } from '../types/competition-category.types';

/**
 * Compute the dancer's age in completed years on a given reference date.
 *
 * Both `dateOfBirth` and `referenceDate` may be `Date`s or
 * `YYYY-MM-DD` strings. Returns `null` when `dateOfBirth` is invalid
 * or absent.
 */
export function computeAgeOnDate(
  dateOfBirth: string | Date | null | undefined,
  referenceDate: string | Date | null | undefined,
): number | null {
  if (!dateOfBirth) return null;

  const dob = toDate(dateOfBirth);
  if (!dob) return null;

  const ref = toDate(referenceDate) ?? new Date();

  let age = ref.getUTCFullYear() - dob.getUTCFullYear();
  const m = ref.getUTCMonth() - dob.getUTCMonth();
  if (m < 0 || (m === 0 && ref.getUTCDate() < dob.getUTCDate())) {
    age--;
  }
  return age;
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  // Date-only string (YYYY-MM-DD): treat as UTC midnight so we get a stable
  // age calculation independent of the runtime's local timezone.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const d = new Date(`${value}T00:00:00Z`);
    return isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Map a dancer's completed-years age to their competition age division
 * (their "category" in the styles/categories separation).
 *
 * Product-owner fixed mapping (2026-06-04): under 9 → mini (including
 * under 5), 9-11 junior, 12-14 teen, 15-17 intermediate, 18+ senior with
 * NO upper cap. Deliberately ignores `AGE_DIVISION_RANGES`' senior cap of
 * 29 — those ranges describe per-style-entry bounds for organizers, not
 * dancer assignment.
 */
export function ageToDivision(age: number): CompetitionAgeDivision {
  if (age < 9) return 'mini';
  if (age <= 11) return 'junior';
  if (age <= 14) return 'teen';
  if (age <= 17) return 'intermediate';
  return 'senior';
}

/**
 * Resolve the date on which a dancer's age (and thus division) is computed
 * for a competition: the audition's explicit `age_cut_date` when set, else
 * January 1st of the event year (from the first audition date), else
 * January 1st of the current year.
 *
 * Returns a `YYYY-MM-DD` string.
 */
export function resolveAgeReferenceDate(args: {
  ageCutDate?: string | null | undefined;
  /** `audition_dates[0].start_date` — the first day of the event. */
  firstEventDate?: string | null | undefined;
  now?: Date;
}): string {
  const { ageCutDate, firstEventDate, now = new Date() } = args;
  if (ageCutDate) return ageCutDate;

  const eventDate = firstEventDate ? toDate(firstEventDate) : null;
  const eventYear = (eventDate ?? now).getUTCFullYear();
  return `${eventYear}-01-01`;
}

/**
 * DOB + competition context → the dancer's auto-assigned age division.
 * Returns `null` when the date of birth is absent or invalid.
 */
export function resolveDancerDivision(args: {
  dateOfBirth: string | null | undefined;
  ageCutDate?: string | null | undefined;
  firstEventDate?: string | null | undefined;
}): CompetitionAgeDivision | null {
  const referenceDate = resolveAgeReferenceDate({
    ageCutDate: args.ageCutDate,
    firstEventDate: args.firstEventDate,
  });
  const age = computeAgeOnDate(args.dateOfBirth, referenceDate);
  return age == null ? null : ageToDivision(age);
}
